import { DYNAMIC_QUESTION_TYPE } from 'app-constants';
import { v4 as uuidv4 } from 'uuid';
import { signatureFieldType } from '../../../DynamicViewer/components/FieldRenderer/Fields/types';

export const SIGNATURE_MIN_WIDTH = 88;
export const SIGNATURE_MIN_HEIGHT = 20;

export const INITIALS_MIN_WIDTH = 38;
export const INITIALS_MIN_HEIGHT = 15;
export const PAGE_SPACING = 66;

export const shouldHideType = (fieldType, width) => {
  const { Initials, DateSigned, Date, TextBox, FullName, Address, Phone, Number, Currency } =
    DYNAMIC_QUESTION_TYPE;

  if (fieldType === Initials && width < 75) return true;
  else if ((fieldType === DateSigned || fieldType === Date) && width < 126) return true;
  else if (fieldType === TextBox && width < 84) return true;
  else if (fieldType === FullName && width < 102) return true;
  else if (fieldType === Phone && width < 133) return true;
  else if (fieldType === Address && width < 85) return true;
  else if (fieldType === Number && width < 85) return true;
  else if (fieldType === Currency && width < 93) return true;
};

export const calculateGroupSize = (field, scale) => {
  const { options } = field as any;

  const maxRight = Math.max(
    ...options.map((opt) => !opt.isDeleted && (opt.x < 0 ? 0 : opt.x) + opt.width * scale),
  );

  const maxBottom = Math.max(
    ...options.map((opt) => !opt.isDeleted && (opt.y < 0 ? 0 : opt.y) + opt.height * scale),
  );
  return { width: maxRight + 20, height: maxBottom + 40 };
};

export const getCopiedRadioField = (field) => {
  const newOptions: any[] = [];
  const radioField = { ...field };

  const omittedKeys = [
    'isUpdated',
    'isGroupResized',
    'isHighlighted',
    'isDeleted',
    'questionValidationId',
    'groupOptionUUID',
    'groupDragged',
    'groupBorderWidth',
    'validationId',
  ];

  omittedKeys.forEach((key) => delete radioField[key]);

  radioField.options.forEach((optionItem) => {
    const { id, isUpdated, isDeleted, ...item } = optionItem;

    if (isDeleted) return;

    newOptions.push({
      ...item,
      UUID: uuidv4(),
    });
  });

  radioField.options = newOptions;

  return radioField;
};

export const canChangeRole = (type, answer) => {
  return !signatureFieldType.includes(type) || !answer;
};

export const getResizeProps = (isStrikeThrough) => {
  const rndResizeProps = {
    ...(isStrikeThrough && {
      enableResizing: {
        bottom: false,
        bottomLeft: false,
        bottomRight: false,
        left: true,
        right: true,
        top: false,
        topLeft: false,
        topRight: false,
      },
    }),
  };

  return rndResizeProps;
};

export const getWidthHeight = (width, height, field) => {
  const signatureField = field.type === DYNAMIC_QUESTION_TYPE.Signature;
  const initialField = field.type === DYNAMIC_QUESTION_TYPE.Initials;

  if (signatureField) {
    width = width < SIGNATURE_MIN_WIDTH ? SIGNATURE_MIN_WIDTH : width;
    height = height < SIGNATURE_MIN_HEIGHT ? SIGNATURE_MIN_HEIGHT : height;
  } else if (initialField) {
    width = width < INITIALS_MIN_WIDTH ? INITIALS_MIN_WIDTH : width;
    height = height < INITIALS_MIN_HEIGHT ? INITIALS_MIN_HEIGHT : height;
  }

  return { width, height };
};

export const prepareTemplateAnswers = (answers) => {
  if (!answers?.length) return {};

  const fieldAnswers = [...answers];

  const documentAnswers = {};
  fieldAnswers.forEach((item) => {
    const { UUID, answer } = item;

    documentAnswers[UUID] = { Answer: answer, UUID, DefaultAnswer: '' };
  });

  return documentAnswers;
};

export const isInvalidDrag = (e, d, scale, field, pageHeight) => {
  const { x, y } = d;

  const updatedX = x / scale;
  const updatedY = y / scale;
  const isSame = updatedX === field.x && updatedY === field.y;

  const isGroupField = field.type === DYNAMIC_QUESTION_TYPE.RadioGroup;
  return (isGroupField && isSame) || isPageOverflow(e, d, pageHeight, scale, field);
};

export const isPageOverflow = (event, data, pageHeight, scale, field) => {
  const { y } = data;

  const updatedY = y / scale;
  const isDifferentPage = event.target?.id.includes('pageContainerDrop');

  if (updatedY < 0) {
    let difference = y * -1 - field.height * scale;

    difference = difference < 0 ? difference * -1 : difference;

    return difference < PAGE_SPACING;
  } else {
    return y > pageHeight && !isDifferentPage;
  }
};

export const manageDragStop = (dragStopProps) => {
  const { e, y, field, answer, pageIndex, pageHeight, documentIndex, isPreviousPage } =
    dragStopProps;

  let newDocIndex, newPageIndex, newY;

  const isCheckbox = field.type === DYNAMIC_QUESTION_TYPE.CheckBox;
  const isGroupField = field.type === DYNAMIC_QUESTION_TYPE.RadioGroup;

  if (isPreviousPage) {
    newDocIndex = pageIndex === 0 ? documentIndex - 1 : documentIndex;

    const childElement: any = document.getElementById(`pdf-doc-${newDocIndex}`);
    newPageIndex = pageIndex !== 0 ? pageIndex - 1 : childElement?.childElementCount - 1;

    const previousPageInfo = document
      .getElementById(`pageContainerDrop-${newDocIndex}-${newPageIndex}`)
      ?.getBoundingClientRect();

    const modifiedY = y * -1;

    newY = (previousPageInfo?.height || 0) - (modifiedY - PAGE_SPACING);
  } else {
    const indexDetails = e.target.id.split('pageContainerDrop-')[1];
    [newDocIndex, newPageIndex] = indexDetails.split('-');

    newDocIndex = +newDocIndex;
    newPageIndex = +newPageIndex;
    newY = y - (pageHeight + PAGE_SPACING);
  }

  let questionUUID = field.UUID;

  let fieldAnswer;

  const isDifferentDocument = newDocIndex !== documentIndex;

  if (isDifferentDocument) {
    questionUUID = uuidv4();
    let answerDetails = answer;

    if (!isGroupField && answerDetails) {
      if (isCheckbox) {
        answerDetails = JSON.stringify([questionUUID]);
      }

      fieldAnswer = {
        answer: answerDetails,
        UUID: questionUUID,
        documentIndex: newDocIndex,
      };
    }
  }

  return { fieldAnswer, newY, newDocIndex, newPageIndex, questionUUID, isDifferentDocument };
};
